import { useEffect, useState } from "react";
import { Check, MessageCircle, Phone } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { Input, TextArea, Consent, Honeypot } from "@/components/site/Form";
import { SuccessNote, useLeadForm } from "@/components/vehicle/Forms";
import { NotFound } from "@/pages/Misc";
import { api } from "@/lib/api";
import { useSite } from "@/lib/site";
import { useSeo } from "@/hooks/useSeo";

const STEPS = [
  ["Send a booking request", "Tell us about your vehicle and the work it needs using the form below."],
  ["We reply with a quote", "We come back to you with a price and the dates available."],
  ["You confirm", "Nothing is booked until you have agreed the quote and the date."],
];

// While the page is a draft it is shown only to a signed-in admin.
function useDraftAccess(live) {
  const [admin, setAdmin] = useState(null);
  useEffect(() => {
    if (live) return;
    if (!localStorage.getItem("c4x4_admin_token")) { setAdmin(false); return; }
    api.get("/auth/me").then(() => setAdmin(true)).catch(() => setAdmin(false));
  }, [live]);
  return live ? true : admin;
}

export default function Servicing() {
  const site = useSite();
  const live = !!site.servicing_live;
  const allowed = useDraftAccess(live);
  const services = Array.isArray(site.servicing_services) ? site.servicing_services.filter(Boolean) : [];
  useSeo({ title: "4x4 & SUV Servicing | Cirencester 4x4", description: "Servicing for 4x4s and SUVs near Cirencester, quoted individually. Send a booking request and we will reply with a price and a date.", path: "/servicing" });
  if (site.servicing_live === undefined || allowed === null) return <main className="min-h-screen" />;
  if (!allowed) return <NotFound />;
  const wa = `https://wa.me/${(site.whatsapp || "").replace(/\D/g, "")}`;
  return (
    <main data-testid="servicing-page">
      {!live && <p className="fixed inset-x-0 top-20 z-40 bg-[#8E2F25] px-5 py-2 text-center text-sm font-semibold text-white" data-testid="servicing-draft-banner">Draft: only you can see this page. Publish it in Admin → Site content.</p>}
      <PageHero eyebrow="Servicing" title="Servicing for your 4x4." sub="Quoted individually. Booked by request." image="/img/def16c_front_left.webp">
        <p className="mt-6 max-w-xl text-base leading-relaxed text-charcoal/80" data-testid="servicing-intro">{site.servicing_intro}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#booking" className="btn btn-primary" data-testid="servicing-cta-book">Request a booking</a>
          {site.whatsapp && <a href={wa} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="servicing-cta-whatsapp"><MessageCircle className="h-4 w-4" /> WhatsApp</a>}
        </div>
      </PageHero>
      <ServiceList services={services} />
      <Steps />
      <BookingForm services={services} site={site} />
    </main>
  );
}

const ServiceList = ({ services }) => (
  <section className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-24" data-testid="servicing-list">
    <h2 className="font-serif text-4xl text-charcoal">What we offer</h2>
    <ul className="mt-10 grid gap-x-12 gap-y-5 sm:grid-cols-2">
      {services.map((s, i) => (
        <Reveal key={s} delay={i * 0.05}>
          <li className="flex items-center gap-4 border-b border-stone pb-5 text-lg" data-testid={`servicing-item-${i}`}>
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-forest text-paper"><Check className="h-4 w-4" /></span>{s}
          </li>
        </Reveal>
      ))}
    </ul>
    <p className="mt-8 text-sm text-charcoal/75">Every job is quoted individually. Prices on request.</p>
  </section>
);

const Steps = () => (
  <section className="border-y border-stone bg-forest text-paper" data-testid="servicing-steps">
    <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-16 sm:px-8 md:grid-cols-3 lg:px-12 lg:py-20">
      {STEPS.map(([t, d], i) => (
        <Reveal key={t} delay={i * 0.08}>
          <p className="font-serif text-5xl italic text-stone">{i + 1}</p>
          <h2 className="mt-3 text-lg font-semibold">{t}</h2>
          <p className="mt-2 text-sm leading-relaxed text-paper/85">{d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

function BookingForm({ services, site }) {
  const { f, setF, errors, bind, busy, done, submit } = useLeadForm({ vehicle: "", registration: "", mileage: "", work: [], date: "" });
  const toggle = (s) => setF({ ...f, work: f.work.includes(s) ? f.work.filter((x) => x !== s) : [...f.work, s] });
  const onSubmit = (e) => {
    e.preventDefault();
    const message = [`Vehicle: ${f.vehicle}`, `Registration: ${f.registration}`, `Mileage: ${f.mileage || "not given"}`,
      `Work needed: ${f.work.join(", ") || "not specified"}`, `Preferred date: ${f.date || "flexible"}`, f.message && `Notes: ${f.message}`].filter(Boolean).join("\n");
    const extra = {};
    if (!f.vehicle.trim()) extra.vehicle = "Please enter the make and model";
    if (!f.registration.trim()) extra.registration = "Please enter the registration";
    submit({ enquiry_type: "Servicing booking", lead_source: "Website - servicing page", message }, extra);
  };
  return (
    <section id="booking" className="mx-auto max-w-[1440px] scroll-mt-28 px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <h2 className="font-serif text-4xl text-charcoal">Request a booking</h2>
          <p className="mt-4 text-base text-charcoal/80">We reply with a quote and available dates. Nothing is booked until you confirm.</p>
          <a href={`tel:${site.phone_href}`} className="link-u mt-6 inline-flex items-center gap-2 text-forest" data-testid="servicing-call"><Phone className="h-4 w-4" />{site.phone}</a>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          {done ? <SuccessNote title="Thank you, request received." testid="servicing-success">We will reply with a quote and available dates.</SuccessNote> : (
            <form noValidate onSubmit={onSubmit} className="relative space-y-5" data-testid="servicing-form">
              <Honeypot value={f.website} onChange={(v) => setF({ ...f, website: v })} />
              <div className="grid gap-5 sm:grid-cols-2">
                <Input id="sv-name" label="Name" required autoComplete="name" {...bind("name")} />
                <Input id="sv-email" label="Email" type="email" required autoComplete="email" {...bind("email")} />
                <Input id="sv-phone" label="Telephone" type="tel" required autoComplete="tel" {...bind("phone")} />
                <Input id="sv-vehicle" label="Make and model" required placeholder="e.g. Land Rover Defender 90" {...bind("vehicle")} />
                <Input id="sv-registration" label="Registration" required {...bind("registration")} />
                <Input id="sv-mileage" label="Mileage" inputMode="numeric" {...bind("mileage")} />
              </div>
              <WorkPicker services={services} picked={f.work} onToggle={toggle} />
              <Input id="sv-date" label="Preferred date" type="date" {...bind("date")} />
              <TextArea id="sv-message" label="Anything else we should know?" {...bind("message")} />
              <Consent id="sv-consent" checked={f.consent} onChange={(v) => setF({ ...f, consent: v })} error={errors.consent} />
              <button type="submit" disabled={busy} className="btn btn-primary" data-testid="servicing-submit">{busy ? "Sending…" : "Send booking request"}</button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const WorkPicker = ({ services, picked, onToggle }) => (
  <fieldset>
    <legend className="mb-3 block text-[0.78rem] font-semibold uppercase tracking-[0.12em] text-charcoal/80">Work needed</legend>
    <div className="grid gap-3 sm:grid-cols-2">
      {services.map((s, i) => (
        <label key={s} className="flex cursor-pointer items-center gap-3 border border-stone px-4 py-3 text-sm transition-colors hover:border-forest">
          <input type="checkbox" checked={picked.includes(s)} onChange={() => onToggle(s)} className="h-4 w-4 accent-[#16352C]" data-testid={`servicing-work-${i}`} />{s}
        </label>
      ))}
    </div>
  </fieldset>
);
